/** @jsxImportSource @emotion/react */
import { Box } from '@deity/falcon-ui';
import { keyframes } from '@emotion/react';
import type { ReactNode } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import Portal from '../Portal/Portal';

type Alignment = 'center' | 'flex-end' | 'flex-start';

interface IModalProps {
  id: string;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  zIndex?: number;
  maxWidth?: number;
  maxHeight?: number;
  margin?: number;
  alignVertical?: Alignment;
  alignHorizontal?: Alignment;
  fade?: boolean;
  fadeDuration?: number;
  allowStacking?: boolean;
  defaultOpened?: boolean;
  children: ReactNode;
  onOpenChange?: (state: boolean) => void;
}
export const Modal = forwardRef<ReactNode, IModalProps>(
  (
    {
      id,
      children,
      defaultOpened = false,
      zIndex = 999999,
      maxWidth = 800,
      maxHeight = 700,
      fade = true,
      fadeDuration = 300,
      closeOnEsc = true,
      closeOnOverlayClick = true,
      alignVertical = 'center',
      alignHorizontal = 'center',
      allowStacking,
      margin = 0,
      onOpenChange
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpened);

    const onClose = useCallback(() => {
      setIsOpen(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsOpen(true);
        },
        close: () => {
          setIsOpen(false);
        }
      }),
      []
    );

    const handleEscape = useCallback(event => {
      if (event.keyCode === 27 && closeOnEsc) {
        setIsOpen(false);
      }
    }, []);

    useEffect(() => {
      if (onOpenChange) {
        onOpenChange(isOpen);
      }
    }, [isOpen]);

    useEffect(() => {
      if (isOpen) {
        document.addEventListener('keydown', handleEscape, false);
      }

      return () => {
        document.removeEventListener('keydown', handleEscape, false);
        window.removeEventListener('scroll', handleEscape, false);
      };
    }, [handleEscape, isOpen]);

    return (
      <Portal id={id}>
        {isOpen ? (
          <Box
            css={{
              ...MODAL_CONTAINER,
              zIndex,
              opacity: `${!fade ? 1 : 0}`,
              animation: `${!fade ? 'none' : `${fadeIn} ${fadeDuration}ms linear`}`,
              alignItems: alignVertical,
              justifyContent: alignHorizontal
            }}
          >
            <Box
              css={{
                width: '100%',
                height: '100%',
                maxWidth,
                maxHeight,
                zIndex: zIndex + 1,
                margin
              }}
            >
              {children}
            </Box>
            <Box css={MODAL_OVERLAY} onClick={() => (closeOnOverlayClick ? onClose() : {})} />
          </Box>
        ) : null}
      </Portal>
    );
  }
);

const fadeIn = keyframes`
  0% {
    animation-timing-function: cubic-bezier(0.2242, 0.7499, 0.3142, 0.8148);
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const MODAL_CONTAINER: any = {
  position: 'fixed',
  overflow: 'hidden',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.65)',
  width: '100vw',
  height: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  opacity: 0,
  animation: `${fadeIn} .2s 1 linear`,
  animationFillMode: 'forwards'
};

const MODAL_OVERLAY: any = {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0
};
